import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import CustomerList from './components/CustomerList';
import CustomerForm from './components/CustomerForm';
import ConsigneeList from './components/ConsigneeList';
import ConsigneeForm from './components/ConsigneeForm';
import InvoiceList from './components/InvoiceList';
import InvoiceForm from './components/InvoiceForm';
import UserManagement from './components/UserManagement';
import AuditLog from './components/AuditLog';
import ChangePassword from './components/ChangePassword';

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Main Layout Component
function MainLayout({ children }) {
  const { user, logout, isAdmin } = useAuth();

  return (
    <div className="App">
      <nav className="navbar">
        <div className="navbar-content">
          <div className="navbar-brand">
            <img
              src="/assets/jle-logo.png"
              alt="JLE Trucking Logo"
              className="navbar-logo"
            />
            <div className="navbar-text">
              <h1>JLE TRUCKING, INC</h1>
              <p>Invoice Management System</p>
            </div>
          </div>
          <div className="navbar-links">
            <Link to="/">Dashboard</Link>
            <Link to="/invoices">Invoices</Link>
            <Link to="/customers">Customers</Link>
            <Link to="/consignees">Consignees</Link>
            {isAdmin() && <Link to="/users">Users</Link>}
            {isAdmin() && <Link to="/audit-log">Activity Log</Link>}
          </div>
          <div className="navbar-user">
            <span>Welcome, {user?.name}</span>
            <button onClick={logout} className="btn btn-logout">
              Logout
            </button>
          </div>
        </div>
      </nav>

      <main className="main-content">
        {children}
      </main>

      <footer className="footer">
        <p>&copy; 2024 JLE TRUCKING, INC. All rights reserved.</p>
      </footer>
    </div>
  );
}

function AppContent() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />

      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        }
      />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Dashboard />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CustomerList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/customers/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CustomerForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/customers/edit/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <CustomerForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/consignees"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ConsigneeList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/consignees/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ConsigneeForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/consignees/edit/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ConsigneeForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InvoiceList />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/invoices/new"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InvoiceForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/invoices/edit/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InvoiceForm />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <MainLayout>
              <UserManagement />
            </MainLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/audit-log"
        element={
          <ProtectedRoute>
            <MainLayout>
              <AuditLog />
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
